import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Package } from "lucide-react";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";

const TABS = [
  { key: "sealed_pack", label: "Sealed Wax" },
  { key: "sealed_box",  label: "Boxes" },
  { key: "sealed_case", label: "Cases" },
];

export default function SealedWax() {
  const [tab, setTab] = useState("sealed_pack");
  const [counts, setCounts] = useState({ sealed_pack: 0, sealed_box: 0, sealed_case: 0 });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState({});

  useEffect(() => {
    document.title = "Sealed Wax | Card Lobster";
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
    // Load counts for tab badges (one call per category, small payload)
    Promise.all(TABS.map((t) => api.get(`/products?category=${t.key}`)))
      .then((results) => {
        const c = {};
        TABS.forEach((t, i) => { c[t.key] = results[i].data.length; });
        setCounts(c);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    api.get(`/products?category=${tab}`)
      .then((r) => setItems(r.data))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [tab]);

  const kicker = settings.sealed_kicker || "Wholesale · Sealed Product";
  const title = settings.sealed_title || "Sealed Wax, Boxes & Cases";
  const subtitle = settings.sealed_subtitle || "Fresh from the manufacturer. Perfect for breakers, resellers, and collectors chasing the biggest hits.";

  return (
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-16" data-testid="sealed-page">
      <div className="max-w-3xl">
        <div className="flex items-center gap-3 text-label text-[var(--cl-primary)]">
          <Package size={16} /> <span>{kicker}</span>
        </div>
        <h1 className="font-display text-5xl sm:text-7xl mt-2">{title}</h1>
        <p className="text-zinc-300 text-lg mt-4">{subtitle}</p>
      </div>

      <div className="mt-10 flex flex-wrap gap-2 border-b border-white/10">
        {TABS.map((t) => {
          const active = t.key === tab;
          return (
            <button key={t.key} onClick={() => setTab(t.key)}
                    data-testid={`sealed-tab-${t.key}`}
                    className={`px-4 py-3 text-label transition-colors border-b-2 -mb-px ${active ? "text-white border-[var(--cl-primary)]" : "text-zinc-500 border-transparent hover:text-zinc-300"}`}>
              {t.label} <span className="ml-1 text-xs opacity-60">{counts[t.key] || 0}</span>
            </button>
          );
        })}
      </div>

      <div className="mt-8">
        {loading && <div className="text-zinc-500 text-sm">Loading…</div>}
        {!loading && items.length === 0 && (
          <div className="border border-dashed border-white/10 rounded-lg py-20 text-center" data-testid="sealed-empty">
            <Package size={40} className="mx-auto text-[var(--cl-orange)]" />
            <h2 className="font-display text-3xl mt-4">Restocking soon</h2>
            <p className="text-zinc-400 text-sm mt-3 max-w-md mx-auto">
              New sealed product drops after most live auctions. Follow us on Whatnot to catch it first.
            </p>
            <div className="flex flex-wrap justify-center gap-3 mt-6">
              <Link to="/mystery-packs" className="btn-primary">Mystery Packs</Link>
              <Link to="/live" className="btn-outline">Watch Live</Link>
            </div>
          </div>
        )}
        {!loading && items.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5" data-testid="sealed-grid">
            {items.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        )}
      </div>
    </div>
  );
}
